import { useAuthStore } from '../../store/authStore';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { clientPortalAPI } from '../../api';
import { Loader2 } from 'lucide-react';

const formatCurrency = (n) =>
  typeof n === 'number' && !Number.isNaN(n)
    ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n)
    : '—';

const statCardLinkClass =
  'block rounded-[var(--ds-radius-lg)] no-underline text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900';

export default function ClientDashboard() {
  const { user } = useAuthStore();

  const { data: invoicePayload, isLoading: invoicesLoading } = useQuery({
    queryKey: ['client-portal', 'my-invoices'],
    queryFn: () => clientPortalAPI.getMyInvoices(),
  });

  const { data: estimates = [], isLoading: estimatesLoading } = useQuery({
    queryKey: ['client-portal', 'my-estimates'],
    queryFn: () => clientPortalAPI.getMyEstimates(),
  });

  const invoices = invoicePayload?.invoices ?? [];
  const outstandingTotal = invoices.reduce((sum, inv) => sum + (Number(inv.balanceDue) || 0), 0);
  const invoiceCount = invoices.length;
  const estimateCount = estimates.length;
  const statsLoading = invoicesLoading || estimatesLoading;

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
            Client Portal
          </h1>
          <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
            View your invoices, estimates, and certificates
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-h1 font-semibold text-[var(--ds-text-primary)] mb-2">
              Welcome, {user?.firstName} {user?.lastName}!
            </h2>
            <p className="text-body text-[var(--ds-text-secondary)]">
              Client Portal Access
            </p>
          </CardContent>
        </Card>

        {/* Account Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Link to="/client/invoices" className={statCardLinkClass} aria-label="View your invoices">
            <Card className="h-full cursor-pointer transition-shadow hover:shadow-md">
              <CardContent className="p-6">
                <h3 className="text-meta font-medium text-[var(--ds-text-muted)]">Outstanding balance</h3>
                <p className="text-3xl font-bold text-[var(--ds-text-primary)] mt-2 min-h-[2.25rem] flex items-center">
                  {statsLoading ? (
                    <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-text-muted)]" />
                  ) : (
                    formatCurrency(outstandingTotal)
                  )}
                </p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/client/estimates" className={statCardLinkClass} aria-label="View your estimates">
            <Card className="h-full cursor-pointer transition-shadow hover:shadow-md">
              <CardContent className="p-6">
                <h3 className="text-meta font-medium text-[var(--ds-text-muted)]">Estimates</h3>
                <p className="text-3xl font-bold text-[var(--ds-text-primary)] mt-2 min-h-[2.25rem] flex items-center">
                  {statsLoading ? (
                    <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-text-muted)]" />
                  ) : (
                    estimateCount
                  )}
                </p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/client/invoices" className={statCardLinkClass} aria-label="View your invoices">
            <Card className="h-full cursor-pointer transition-shadow hover:shadow-md">
              <CardContent className="p-6">
                <h3 className="text-meta font-medium text-[var(--ds-text-muted)]">Invoices</h3>
                <p className="text-3xl font-bold text-[var(--ds-text-primary)] mt-2 min-h-[2.25rem] flex items-center">
                  {statsLoading ? (
                    <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-text-muted)]" />
                  ) : (
                    invoiceCount
                  )}
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        
      </div>
    </Layout>
  );
}

